import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router";
import InfoIcon from "@material-ui/icons/Info";
import SaveIcon from "@material-ui/icons/Save";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import M_DataStateRenderer from "../../../Molecules/Common/DataStateRenderer/M_DataStateRenderer.jsx";
import LoaderSkeleton from "../../../Molecules/Common/LoaderSkeleton";
import { validateProvisionDraft } from "../../../../../domain/provision/provision.rules";
import {
  api,
  normaliseProvision,
  buildUpsertPayload,
} from "./Provision.helper";
import ProvisionDetailsSection from "./ProvisionDetailsSection";
import UploadDcfSection from "./UploadDcfSection";
import ProfessionalsSection from "./ProfessionalsSection";
import ScenariosSection from "./ScenariosSection";
import UploadedDcfFilesTable from "./UploadedDcfFilesTable";
import NotesSection from "./NotesSection";
import usePageSecurity from "../../../../../Utils/Helpers/usePageSecurity";
import { pageSecurityActions } from "../../../../../Utils/Helpers/pageSecurity";
import useStyles from "./Provision.styles";
import A_SecuredAction from "../../../Atoms/Security/A_SecuredAction";

/**
 * Provision card (registered as `minerva.loan-provision`, Story 56425) — a self-contained tab
 * inside the loan record. Resolves loanId from an explicit prop, this card's own dev route
 * (`/loans/:loanId/provision`), or the generic P_PageLayout route `/:pageUrl/:crmId/:templateId?`
 * (where `crmId` IS the loanId), matching the Professional Details card's precedent exactly —
 * see ProfessionalDetails.jsx for the identical reasoning.
 *
 * Provision Details/Professionals/Notes are held as a local draft and saved together via
 * upsertProvision (the contract's editable ProvisionUpsert body). Scenarios and DCF documents
 * each have their own dedicated endpoints and mutate immediately — see provisionApi.js and
 * ScenariosSection/UploadDcfSection/UploadedDcfFilesTable.
 */
const Provision = ({ loanId: loanIdProp }) => {
  const classes = useStyles();
  const routeParams = useParams();
  const loanId = loanIdProp || routeParams.loanId || routeParams.crmId;
  const { canUpdate } = usePageSecurity();

  const [provision, setProvision] = useState(null);
  const [detailsDraft, setDetailsDraft] = useState(null);
  const [professionalsDraft, setProfessionalsDraft] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState([]);
  const [saveError, setSaveError] = useState(null);
  // ProvisionDetailsSection/ProfessionalsSection/NotesSection are built on A_TextField's
  // uncontrolled `defaultValue` (matching the app-wide convention — see RoleEditFields.jsx,
  // QuotesEditor.jsx). Re-rendering with a new draft object alone won't refresh what an
  // uncontrolled input displays, so bump this to force-remount them whenever we programmatically
  // reset the drafts (Cancel, or a fresh value back from Save) — same fix as
  // RepositoryList.jsx's `resetToken`.
  const [draftKey, setDraftKey] = useState(0);

  const resetDrafts = (loaded) => {
    setDetailsDraft({
      provisionAmount: loaded.details.provisionAmount,
      provisionApprovalDate: loaded.details.provisionApprovalDate,
      gceNetOfProvision: loaded.details.gceNetOfProvision,
    });
    setProfessionalsDraft(loaded.professionals);
    setNotesDraft(loaded.notes || "");
    setDraftKey((prev) => prev + 1);
  };

  const load = useCallback(() => {
    if (!loanId) return;
    setLoading(true);
    setError(null);
    api
      .getProvision(loanId)
      .then((raw) => {
        const normalised = normaliseProvision(loanId, raw);
        setProvision(normalised);
        resetDrafts(normalised);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, [loanId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!loanId) {
    return (
      <div className={classes.validationSummary}>
        <InfoIcon fontSize="inherit" /> Provision needs a loan to show. No
        loanId was provided to this card.
      </div>
    );
  }

  /* Two independent gates: the loan's CRR decides whether provision may change
     at all, the page config decides whether this user may change it. Every
     section below takes the combined answer. */
  const editableByRule = Boolean(provision?.editable);
  const editable = editableByRule && canUpdate;

  const handleDiscard = () => {
    if (provision) resetDrafts(provision);
    setMissing([]);
    setSaveError(null);
  };

  const handleSave = async () => {
    const { valid, missing: problems } = validateProvisionDraft(
      detailsDraft,
      notesDraft
    );
    if (!valid) {
      setMissing(problems);
      return;
    }
    setMissing([]);
    setSaveError(null);
    setSaving(true);
    try {
      const payload = buildUpsertPayload(
        detailsDraft,
        professionalsDraft,
        notesDraft
      );
      const saved = await api.upsertProvision(loanId, payload);
      const normalised = normaliseProvision(loanId, saved);
      setProvision(normalised);
      resetDrafts(normalised);
    } catch (err) {
      setSaveError(err);
      setMissing(
        err.field
          ? [`${err.field}: ${err.message}`]
          : [err.message || "Save failed. Please try again."]
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className={classes.headerBar}>
        {/* Heading only — no loan identifier here. The breadcrumb above already names the
            loan, and `loanId` is the loan GUID (not the user-facing loan number), so printing it
            was noise at best; anything else about the loan belongs on Loan Overview. */}
        <div>
          <h1 className={classes.title}>Provision</h1>
        </div>
        <div className={classes.actions}>
          <A_Button
            label="Cancel"
            onClick={handleDiscard}
            disabled={loading || saving || !editable}
          />
          <A_SecuredAction permission={pageSecurityActions.UPDATE}>
            <A_Button
              label="Save"
              premiumVariant="primary"
              startIcon={<SaveIcon fontSize="small" />}
              onClick={handleSave}
              disabled={loading || saving || !editable}
            />
          </A_SecuredAction>
        </div>
      </div>

      <M_DataStateRenderer
        loading={loading}
        error={error}
        data={provision}
        skeleton={<LoaderSkeleton />}
      >
        {provision && detailsDraft && professionalsDraft && (
          <>
            {!editable && (
              <div className={classes.editNote}>
                <InfoIcon fontSize="inherit" />
                <span>
                  {editableByRule
                    ? "Provision is read-only because your access to this page does not include edit rights. Existing values are shown below but cannot be changed."
                    : "Provision is read-only for this loan (requires CRR ≥ 6). Existing values are shown below but cannot be changed."}
                </span>
              </div>
            )}
            {editable && (
              <div className={classes.editNote}>
                <InfoIcon fontSize="inherit" />
                <span>
                  Provision is editable while CRR &ge; 6. All additions,
                  deletions and amendments in this tab are audited (who and
                  when).
                </span>
              </div>
            )}

            {missing.length > 0 && (
              <div className={classes.validationSummary}>
                <InfoIcon fontSize="inherit" /> Please fix the following:{" "}
                {missing.join("; ")}.
              </div>
            )}
            {saveError && !missing.length && (
              <div className={classes.validationSummary}>
                {saveError.message ||
                  "Something went wrong saving. Please try again."}
              </div>
            )}

            <div className={classes.grid2}>
              <ProvisionDetailsSection
                key={`details-${draftKey}`}
                details={provision.details}
                draft={detailsDraft}
                onChange={setDetailsDraft}
                editable={editable}
              />
              <UploadDcfSection
                loanId={loanId}
                editable={editable}
                onUploaded={(document) =>
                  setProvision((prev) => ({
                    ...prev,
                    dcfDocuments: [...prev.dcfDocuments, document],
                  }))
                }
              />
            </div>

            <div style={{ height: 22 }} />

            <ProfessionalsSection
              key={`professionals-${draftKey}`}
              draft={professionalsDraft}
              onChange={setProfessionalsDraft}
              editable={editable}
            />

            <div style={{ height: 22 }} />

            <div className={classes.grid2}>
              <ScenariosSection
                loanId={loanId}
                scenarios={provision.scenarios}
                provisionOutcomePct={provision.provisionOutcomePct}
                editable={editable}
                onChange={(scenarios, provisionOutcomePct) =>
                  setProvision((prev) => ({
                    ...prev,
                    scenarios,
                    provisionOutcomePct,
                  }))
                }
              />
              <UploadedDcfFilesTable
                loanId={loanId}
                documents={provision.dcfDocuments}
                editable={editable}
                onDeleted={(documentId) =>
                  setProvision((prev) => ({
                    ...prev,
                    dcfDocuments: prev.dcfDocuments.filter(
                      (d) => d.id !== documentId
                    ),
                  }))
                }
              />
            </div>

            <div style={{ height: 22 }} />

            <NotesSection
              key={`notes-${draftKey}`}
              notes={notesDraft}
              onChange={setNotesDraft}
              editable={editable}
            />
          </>
        )}
      </M_DataStateRenderer>
    </div>
  );
};

Provision.propTypes = {
  loanId: PropTypes.string,
};

export default Provision;
